// frontend/packages/business-console/src/components/layout/nav-config.js
//
// Sidebar grouping for the Business Console. Mirrors the Tools pattern
// (apps/tools-web/src/ui/nav-config.tsx) — labeled groups with a section
// caption and a list of nav items. Routes themselves are still defined in
// pages/pageConfig.js (single source of truth for routing); this file
// only declares which keys live in which group, plus per-item description
// shown as a tooltip in the sidebar.
//
// To surface a new page in the sidebar:
//   1. Add it to Workspace_Pages in pageConfig.js (so the route exists)
//   2. Add its key to one of the groups below
// Items in Workspace_Pages that don't appear in any group are simply not
// rendered in the sidebar (e.g., detail pages with hideFromNav: true).
//
// An item with `feature: '<tenant feature key>'` shows only for tenants who
// have that feature on (GET /v2/features), for opt-in pages that would be
// noise for everyone else. School district mode hides the sales pages (see
// districtMode.js); `districtLabel` renames a group while that mode is on.

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

export const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      {
        key: 'home',
        icon: HomeOutlinedIcon,
        description: 'Quick actions and dashboards',
      },
      {
        key: 'pulse',
        icon: MonitorHeartOutlinedIcon,
        description: 'Heads-up display: money, pipeline, inbox, AI visibility',
      },
    ],
  },
  {
    label: 'Customers',
    districtLabel: 'Messages',
    items: [
      {
        key: 'inbox',
        icon: InboxOutlinedIcon,
        description: 'Contact form messages and replies',
      },
      {
        key: 'crm',
        icon: GroupOutlinedIcon,
        description: 'Leads, customers, and pipeline',
      },
      {
        key: 'quotes',
        icon: RequestQuoteOutlinedIcon,
        description: 'Estimates: drafts, sent, accepted',
      },
      {
        key: 'invoices',
        icon: ReceiptLongOutlinedIcon,
        description: 'Invoices: outstanding, paid, overdue',
      },
      {
        key: 'payments',
        icon: AccountBalanceWalletOutlinedIcon,
        description: 'Balance, payouts, and every payment with fee and net',
      },
      {
        key: 'forms',
        icon: AssignmentOutlinedIcon,
        description:
          'Fill and review workflow forms (intake, inspections, work orders)',
      },
      {
        key: 'scheduling',
        icon: EventAvailableOutlinedIcon,
        description: 'Bookings, approvals, staff availability, and services',
      },
    ],
  },
  {
    label: 'Website',
    items: [
      {
        key: 'website-build',
        icon: RocketLaunchOutlinedIcon,
        description: 'Build your website or pick up where you left off',
      },
      {
        key: 'cms-ai',
        icon: AutoAwesomeRoundedIcon,
        description: 'Change your website by chatting with UpRyzze AI',
      },
      {
        key: 'content',
        icon: DescriptionOutlinedIcon,
        description: 'Write or edit pages, posts, and services with AI',
      },
      {
        key: 'ryzzecast',
        icon: CampaignOutlinedIcon,
        description:
          'Write a post once and send it to your website and channels',
      },
      {
        key: 'site-alerts',
        icon: ReportProblemOutlinedIcon,
        description: 'Post an urgent banner across the top of your website',
        feature: 'emergency_alerts',
      },
      {
        key: 'media',
        icon: PhotoLibraryOutlinedIcon,
        description: 'Image and video library',
      },
      {
        key: 'lead-form',
        icon: ContactMailOutlinedIcon,
        description: 'Your website contact form: funnel, fields, submissions',
      },
      {
        key: 'visitors',
        icon: BarChartOutlinedIcon,
        description:
          'Site analytics: traffic, conversions, revenue attribution',
      },
      {
        key: 'uavs',
        icon: InsightsOutlinedIcon,
        description: 'UpRyzze AI Visibility Score',
      },
    ],
  },
  {
    label: 'Account',
    items: [
      {
        key: 'settings',
        icon: SettingsOutlinedIcon,
        description: 'Profile, payment settings, billing, and AI usage',
      },
    ],
  },
  {
    label: 'Support',
    items: [
      {
        key: 'help',
        icon: HelpOutlineOutlinedIcon,
        description: 'Articles, onboarding, contact support',
      },
    ],
  },
];
